import React from 'react';
import { Loader2, AlertCircle, WifiOff, RefreshCw, XCircle } from 'lucide-react';

/**
 * Props for the LoadingState component
 */
export interface LoadingStateProps {
  /** Main title displayed during loading */
  title?: string;
  /** Subtitle or description text */
  subtitle?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to use a full-screen centered layout */
  fullScreen?: boolean;
  /** Custom spinner color class */
  spinnerColor?: string;
  /** Additional className for the container */
  className?: string;
}

/**
 * A reusable loading state component with spinner and optional text.
 *
 * @example
 * ```tsx
 * <LoadingState title="Loading Profile" subtitle="Please wait..." />
 * <LoadingState size="sm" /> // Compact inline spinner
 * <LoadingState fullScreen title="Loading..." /> // Full page loading
 * ```
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  title,
  subtitle,
  size = 'md',
  fullScreen = false,
  spinnerColor = 'text-primary',
  className = '',
}) => {
  const sizeClasses = {
    sm: { spinner: 'w-6 h-6', title: 'text-sm', subtitle: 'text-xs' },
    md: { spinner: 'w-12 h-12', title: 'text-lg', subtitle: 'text-sm' },
    lg: { spinner: 'w-16 h-16', title: 'text-xl', subtitle: 'text-base' },
  };

  const { spinner, title: titleSize, subtitle: subtitleSize } = sizeClasses[size];

  const content = (
    <div className={`text-center ${className}`}>
      <Loader2 className={`${spinner} ${spinnerColor} mx-auto mb-3 animate-spin`} />
      {title && (
        <h2 className={`${titleSize} font-semibold text-foreground mb-1`}>{title}</h2>
      )}
      {subtitle && (
        <p className={`${subtitleSize} text-muted-foreground`}>{subtitle}</p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        {content}
      </div>
    );
  }

  return content;
};

/**
 * Props for the ErrorState component
 */
export interface ErrorStateProps {
  /** Error message to display */
  message: string;
  /** Error title */
  title?: string;
  /** Whether this is a network error */
  isNetworkError?: boolean;
  /** Callback when retry button is clicked */
  onRetry?: () => void;
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void;
  /** Whether to use a full-screen centered layout */
  fullScreen?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional className for the container */
  className?: string;
  /** Custom retry button text */
  retryText?: string;
  /** Custom dismiss button text */
  dismissText?: string;
}

/**
 * A reusable error state component with optional retry functionality.
 *
 * @example
 * ```tsx
 * <ErrorState
 *   message="Failed to load data"
 *   onRetry={() => refetch()}
 * />
 *
 * <ErrorState
 *   title="Connection Error"
 *   message="Please check your internet connection"
 *   isNetworkError
 *   onRetry={() => refetch()}
 *   fullScreen
 * />
 * ```
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  message,
  title,
  isNetworkError = false,
  onRetry,
  onDismiss,
  fullScreen = false,
  size = 'md',
  className = '',
  retryText = 'Try Again',
  dismissText = 'Dismiss',
}) => {
  const sizeClasses = {
    sm: { icon: 'w-8 h-8', title: 'text-base', message: 'text-sm', button: 'px-3 py-1.5 text-sm' },
    md: { icon: 'w-12 h-12', title: 'text-xl', message: 'text-base', button: 'px-4 py-2 text-sm' },
    lg: { icon: 'w-16 h-16', title: 'text-2xl', message: 'text-lg', button: 'px-5 py-2.5 text-base' },
  };

  const { icon, title: titleSize, message: messageSize, button } = sizeClasses[size];

  const Icon = isNetworkError ? WifiOff : AlertCircle;
  const defaultTitle = isNetworkError ? 'Connection Error' : 'Something Went Wrong';

  const content = (
    <div className={`text-center ${className}`}>
      <Icon className={`${icon} ${isNetworkError ? 'text-amber-500' : 'text-destructive'} mx-auto mb-4`} />
      <h2 className={`${titleSize} font-bold text-foreground mb-2`}>{title || defaultTitle}</h2>
      <p className={`${messageSize} text-muted-foreground mb-4 max-w-md mx-auto`}>{message}</p>
      <div className="flex items-center justify-center gap-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className={`${button} bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2`}
          >
            <RefreshCw className="w-4 h-4" />
            {retryText}
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`${button} bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors inline-flex items-center gap-2`}
          >
            <XCircle className="w-4 h-4" />
            {dismissText}
          </button>
        )}
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        {content}
      </div>
    );
  }

  return content;
};

/**
 * Props for the EmptyState component
 */
export interface EmptyStateProps {
  /** Main title */
  title?: string;
  /** Description text */
  description?: string;
  /** Custom icon component */
  icon?: React.ReactNode;
  /** Action button click handler */
  onAction?: () => void;
  /** Action button text */
  actionText?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional className */
  className?: string;
}

/**
 * A reusable empty state component for when there's no data to display.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="No Results Found"
 *   description="Try adjusting your search criteria"
 *   onAction={() => clearFilters()}
 *   actionText="Clear Filters"
 * />
 * ```
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'No Data',
  description,
  icon,
  onAction,
  actionText = 'Refresh',
  size = 'md',
  className = '',
}) => {
  const sizeClasses = {
    sm: { icon: 'w-8 h-8', title: 'text-base', description: 'text-sm', button: 'px-3 py-1.5 text-sm' },
    md: { icon: 'w-12 h-12', title: 'text-lg', description: 'text-sm', button: 'px-4 py-2 text-sm' },
    lg: { icon: 'w-16 h-16', title: 'text-xl', description: 'text-base', button: 'px-5 py-2.5 text-base' },
  };

  const sizes = sizeClasses[size];

  return (
    <div className={`text-center py-8 ${className}`}>
      {icon ? (
        <div className={`${sizes.icon} text-muted-foreground mx-auto mb-3`}>{icon}</div>
      ) : (
        <div className={`${sizes.icon} bg-muted rounded-full flex items-center justify-center mx-auto mb-3`}>
          <AlertCircle className="w-1/2 h-1/2 text-muted-foreground" />
        </div>
      )}
      <h3 className={`${sizes.title} font-medium text-foreground mb-1`}>{title}</h3>
      {description && (
        <p className={`${sizes.description} text-muted-foreground mb-4 max-w-sm mx-auto`}>{description}</p>
      )}
      {onAction && (
        <button
          onClick={onAction}
          className={`${sizes.button} bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors inline-flex items-center gap-2`}
        >
          <RefreshCw className="w-4 h-4" />
          {actionText}
        </button>
      )}
    </div>
  );
};

/**
 * Props for the AsyncContent wrapper component
 */
export interface AsyncContentProps<T> {
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message (null if no error) */
  error: string | null;
  /** The data (null if not loaded yet) */
  data: T | null;
  /** Whether this is a network error */
  isNetworkError?: boolean;
  /** Retry callback */
  onRetry?: () => void;
  /** Render function for the loaded data */
  children: (data: T) => React.ReactNode;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Custom error component */
  errorComponent?: React.ReactNode;
  /** Custom empty state component */
  emptyComponent?: React.ReactNode;
  /** Props for the default loading state */
  loadingProps?: Omit<LoadingStateProps, 'fullScreen'>;
  /** Props for the default error state */
  errorProps?: Omit<ErrorStateProps, 'message' | 'isNetworkError' | 'onRetry' | 'fullScreen'>;
  /** Whether to use full screen layout for loading/error states */
  fullScreen?: boolean;
  /** Custom check to determine if data is considered empty */
  isEmpty?: (data: T) => boolean;
}

/**
 * A wrapper component that handles loading, error, and empty states automatically.
 * Renders children only when data is available.
 *
 * @example
 * ```tsx
 * const { state, retry } = useAsync(() => fetchUser(userId));
 *
 * <AsyncContent
 *   isLoading={state.isLoading}
 *   error={state.error}
 *   data={state.data}
 *   onRetry={retry}
 * >
 *   {(user) => <UserProfile user={user} />}
 * </AsyncContent>
 * ```
 */
export function AsyncContent<T>({
  isLoading,
  error,
  data,
  isNetworkError = false,
  onRetry,
  children,
  loadingComponent,
  errorComponent,
  emptyComponent,
  loadingProps,
  errorProps,
  fullScreen = false,
  isEmpty,
}: AsyncContentProps<T>): React.ReactElement | null {
  // Loading state
  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return <LoadingState fullScreen={fullScreen} {...loadingProps} />;
  }

  // Error state
  if (error) {
    if (errorComponent) {
      return <>{errorComponent}</>;
    }
    return (
      <ErrorState
        message={error}
        isNetworkError={isNetworkError}
        onRetry={onRetry}
        fullScreen={fullScreen}
        {...errorProps}
      />
    );
  }

  // Empty state check
  const dataIsEmpty = data === null || data === undefined || (isEmpty && isEmpty(data));
  if (dataIsEmpty) {
    if (emptyComponent) {
      return <>{emptyComponent}</>;
    }
    return <EmptyState onAction={onRetry} />;
  }

  // Success state - render children with data
  return <>{children(data as T)}</>;
}

/**
 * A higher-order component that wraps a component with async state handling.
 *
 * @example
 * ```tsx
 * const AsyncUserProfile = withAsyncState<User>(UserProfile);
 *
 * <AsyncUserProfile
 *   isLoading={state.isLoading}
 *   error={state.error}
 *   data={state.data}
 *   onRetry={retry}
 * />
 * ```
 */
export function withAsyncState<T>(
  Component: React.ComponentType<T>
): React.FC<AsyncContentProps<T> & Omit<T, 'data'>> {
  return function WithAsyncState(props: AsyncContentProps<T> & Omit<T, 'data'>) {
    const {
      isLoading,
      error,
      data,
      isNetworkError,
      onRetry,
      loadingComponent,
      errorComponent,
      emptyComponent,
      loadingProps,
      errorProps,
      fullScreen,
      isEmpty,
      ...componentProps
    } = props;

    return (
      <AsyncContent
        isLoading={isLoading}
        error={error}
        data={data}
        isNetworkError={isNetworkError}
        onRetry={onRetry}
        loadingComponent={loadingComponent}
        errorComponent={errorComponent}
        emptyComponent={emptyComponent}
        loadingProps={loadingProps}
        errorProps={errorProps}
        fullScreen={fullScreen}
        isEmpty={isEmpty}
      >
        {(resolvedData) => <Component {...(componentProps as T)} data={resolvedData} />}
      </AsyncContent>
    );
  };
}

export default {
  LoadingState,
  ErrorState,
  EmptyState,
  AsyncContent,
  withAsyncState,
};
